import { underscore } from 'inflected';
import terminology from 'javascripts/terminology';
import type FieldSet from './FieldSet';
import { type CardLayoutModelConstructorType } from './types';
import { type RecordLayout } from 'components/framework/models';
import { type FieldContentsData } from 'javascripts/shared-types';

const ALL_FLAVORS = ['roadmap', 'develop', 'ideas', 'teamwork'] as const;
export type Flavor = (typeof ALL_FLAVORS)[number];

export default class Field {
  /**
   * A method that determines whether the field is available for the given model. Checks the available
   * fields on the record, or other feature flags/etc, to determine whether to show the field or not.
   *
   * @returns {boolean} Whether the field is available for the model.
   */
  static availableForModel(
    modelClass: CardLayoutModelConstructorType,
    recordLayout: RecordLayout
  ): boolean {
    if (this.disableFieldForModelNames.includes(modelClass.typename)) {
      return false;
    }

    // Current flavors
    const activeFlavors = [
      window.currentAccount?.roadmapFlavor && 'roadmap',
      window.currentAccount?.developFlavor && 'develop',
      window.currentAccount?.ideasFlavor && 'ideas',
      window.currentAccount?.teamworkFlavor && 'teamwork',
    ].filter(Boolean);
    const { availableForFlavors } = this;
    if (!availableForFlavors.some(flavor => activeFlavors.includes(flavor))) {
      return false;
    }

    const attributes = this.attributesForModel(modelClass);
    const subqueries = Object.keys(this.subqueriesForModel(modelClass));

    if (attributes.length === 0 && subqueries.length === 0) {
      return false;
    }

    return (
      attributes.every(attribute => modelClass.hasField(attribute)) &&
      subqueries.every(subquery => modelClass.hasField(subquery))
    );
  }

  /**
   * A list of flavors to show the field for.
   */
  static get availableForFlavors(): readonly Flavor[] {
    return ALL_FLAVORS;
  }

  /**
   * Returns a list of models that the field should be explicitly disabled for. This means the field
   * exists in GQL, but we do not want to show the field in the layout editor. The main reason for this
   * is that the legacy card rendering might not yet support the field, so we need to skip it for the
   * specific record type.
   */
  static get disableFieldForModelNames(): string[] {
    return [];
  }

  /**
   * The attributes to select on the model.
   */
  static attributesForModel(
    modelClass: CardLayoutModelConstructorType
  ): string[] {
    return [];
  }

  /**
   * The subqueries to select on the model.
   */
  static subqueriesForModel(
    modelClass: CardLayoutModelConstructorType
  ): Record<string, any> {
    return {};
  }

  constructor(public fieldSet: FieldSet) {
    this.fieldSet = fieldSet;
  }

  get id(): string {
    return this.recordFields.keyForField(underscore(this.key));
  }

  get key() {
    return this.constructor.name;
  }

  get title(): string {
    return 'Field';
  }

  get recordFields() {
    return this.fieldSet.recordFields;
  }

  get modelClass() {
    return this.recordFields.modelClass;
  }

  get terminologyName() {
    return this.recordFields.terminologyName;
  }

  get fields() {
    return this.fields;
  }

  recordTerminology() {
    return terminology(this.terminologyName);
  }

  exampleContents(): FieldContentsData {
    return {};
  }

  contentsForRecord(record): FieldContentsData {
    return {};
  }

  buildScope(query) {
    const { modelClass } = this.recordFields;
    const fields = (this.constructor as typeof Field).attributesForModel(
      modelClass
    );
    const includes = (this.constructor as typeof Field).subqueriesForModel(
      modelClass
    );

    return query.select(fields).deepMerge(includes);
  }
}
